// Curated minimal pairs targeting the 6 main contrasts that trip French ears
// in Norwegian bokmål. IPA in standard East Norwegian. Glosses in French.
// References cross-checked with Norsk Ordbok and NB Uttale e_spoken transcriptions.

import type { MinimalPair } from './hvpt-types.ts';

export const HVPT_PAIRS: MinimalPair[] = [
  // --- /y/ vs /ʉ/ — Norwegian "y" vs "u" ---
  { id: 'h001', contrast: 'y-vs-u', cefr: 'A1.1',
    a: { word: 'ny',  ipa: 'nyː', gloss: 'nouveau' },
    b: { word: 'nu',  ipa: 'nʉː', gloss: 'maintenant (poét.)' },
    note: '« y » est plus avancé et étiré que le « u » norvégien.' },
  { id: 'h002', contrast: 'y-vs-u', cefr: 'A1.2',
    a: { word: 'sy',   ipa: 'syː', gloss: 'coudre' },
    b: { word: 'su',   ipa: 'sʉː', gloss: 'suce' },
    note: '/y/ = lèvres très étirées (≈ français « lu »). /ʉ/ = lèvres avancées, langue centrale.' },
  { id: 'h003', contrast: 'y-vs-u', cefr: 'A1.2',
    a: { word: 'lys',  ipa: 'lyːs', gloss: 'lumière, clair' },
    b: { word: 'lus',  ipa: 'lʉːs', gloss: 'pou' },
    note: '« u » norvégien n’existe pas en français standard.' },
  { id: 'h004', contrast: 'y-vs-u', cefr: 'A1.3',
    a: { word: 'tyr',  ipa: 'tyːr', gloss: 'taureau' },
    b: { word: 'tur',  ipa: 'tʉːr', gloss: 'promenade, voyage' },
    note: 'Très fréquent à Stavanger : « gå tur » = aller se promener.' },
  { id: 'h005', contrast: 'y-vs-u', cefr: 'A2.1',
    a: { word: 'flytte', ipa: 'ˈflʏtːə', gloss: 'déménager' },
    b: { word: 'flutte', ipa: 'ˈflʊtːə', gloss: '(forme dialectale)' },
    note: 'En syllabe courte aussi : /ʏ/ vs /ʊ/.' },

  // --- /ç/ (kj) vs /ʃ/ (sj/skj) — kjøtt vs skjøtt ---
  { id: 'h010', contrast: 'kj-vs-sj', cefr: 'A1.1',
    a: { word: 'kjenne', ipa: 'ˈçɛnːə', gloss: 'connaître' },
    b: { word: 'skjenne', ipa: 'ˈʃɛnːə', gloss: 'gronder' },
    note: '« kj » = son palatal proche du « ich » allemand. « sj » = « ch » français.' },
  { id: 'h011', contrast: 'kj-vs-sj', cefr: 'A1.2',
    a: { word: 'kino',  ipa: 'ˈçiːnʊ', gloss: 'cinéma' },
    b: { word: 'sjino', ipa: 'ˈʃiːnʊ', gloss: '(non-mot, illustration)' },
    note: 'Les jeunes Norvégiens fusionnent souvent /ç/ → /ʃ/ : « kjøkken » → [ʃøkːən].' },
  { id: 'h012', contrast: 'kj-vs-sj', cefr: 'A1.3',
    a: { word: 'kjøre',  ipa: 'ˈçøːrə', gloss: 'conduire' },
    b: { word: 'skjære', ipa: 'ˈʃæːrə', gloss: 'couper' },
    note: 'Pour bien produire /ç/ : sourire, langue contre le palais dur.' },
  { id: 'h013', contrast: 'kj-vs-sj', cefr: 'A2.1',
    a: { word: 'kjede',  ipa: 'ˈçeːdə', gloss: 'chaîne, ennuyer' },
    b: { word: 'skjede', ipa: 'ˈʃeːdə', gloss: 'fourreau, vagin' },
    note: 'Distinction sémantiquement critique.' },

  // --- long vs short vowels ---
  { id: 'h020', contrast: 'long-vs-short', cefr: 'A1.1',
    a: { word: 'tak',  ipa: 'tɑːk', gloss: 'toit' },
    b: { word: 'takk', ipa: 'tɑkː', gloss: 'merci' },
    note: 'Voyelle longue = consonne simple. Voyelle brève = consonne double à l’écrit.' },
  { id: 'h021', contrast: 'long-vs-short', cefr: 'A1.2',
    a: { word: 'leke', ipa: 'ˈleːkə', gloss: 'jouer' },
    b: { word: 'lekke', ipa: 'ˈlɛkːə', gloss: 'fuir (eau)' },
    note: 'Sans la distinction longue/brève, deux mots se confondent à l’oral.' },
  { id: 'h022', contrast: 'long-vs-short', cefr: 'A1.3',
    a: { word: 'sove', ipa: 'ˈsoːvə', gloss: 'dormir' },
    b: { word: 'sovne', ipa: 'ˈsɔvnə', gloss: 's’endormir' },
    note: '« o » long ≈ /oː/, court ≈ /ɔ/ (plus ouvert).' },
  { id: 'h023', contrast: 'long-vs-short', cefr: 'A2.1',
    a: { word: 'vise', ipa: 'ˈviːsə', gloss: 'montrer' },
    b: { word: 'visse', ipa: 'ˈvɪsːə', gloss: 'certaines' },
    note: '/iː/ vs /ɪ/ — court = plus ouvert.' },
  { id: 'h024', contrast: 'long-vs-short', cefr: 'A2.2',
    a: { word: 'fin',  ipa: 'fiːn', gloss: 'fin, élégant' },
    b: { word: 'finn', ipa: 'fɪn', gloss: 'trouve (impératif)' },
    note: 'Souvent confondu : « finn det » (trouve-le) vs « fin det » (= mal formé).' },

  // --- /øː/ vs /oː/ — French ear conflates ---
  { id: 'h030', contrast: 'oe-vs-aa', cefr: 'A1.2',
    a: { word: 'øl',  ipa: 'øl', gloss: 'bière' },
    b: { word: 'ål',  ipa: 'oːl', gloss: 'anguille' },
    note: '« ø » court : lèvres arrondies, langue avancée. « å » : lèvres rondes, langue arrière.' },
  { id: 'h031', contrast: 'oe-vs-aa', cefr: 'A1.3',
    a: { word: 'høre', ipa: 'ˈhøːrə', gloss: 'entendre' },
    b: { word: 'håre', ipa: 'ˈhoːrə', gloss: '(non-standard : se coiffer)' },
    note: 'Pour /øː/, partir de « eu » français puis avancer la langue.' },
  { id: 'h032', contrast: 'oe-vs-aa', cefr: 'A2.1',
    a: { word: 'rødt', ipa: 'røtː', gloss: 'rouge (neutre)' },
    b: { word: 'rått', ipa: 'rɔtː', gloss: 'cru (neutre)' },
    note: 'Distinction lexicale fréquente dans les recettes.' },
  { id: 'h033', contrast: 'oe-vs-aa', cefr: 'A2.2',
    a: { word: 'sønn', ipa: 'sønː', gloss: 'fils' },
    b: { word: 'sånn', ipa: 'sɔnː', gloss: 'comme ça' },
    note: 'Deux mots ultra-fréquents : à bien distinguer.' },

  // --- tonal accents (tone 1 vs tone 2) ---
  { id: 'h040', contrast: 'tone-1-vs-2', cefr: 'A2.1',
    a: { word: 'bønder', ipa: '²bønːər', gloss: 'paysans (toneme 2)' },
    b: { word: 'bønner', ipa: '¹bønːər', gloss: 'haricots, prières (toneme 1)' },
    note: 'Tonème 1 = chute simple. Tonème 2 = chute–remontée (bisyllabique).' },
  { id: 'h041', contrast: 'tone-1-vs-2', cefr: 'A2.2',
    a: { word: 'tanken', ipa: '¹tɑŋkən', gloss: 'le réservoir' },
    b: { word: 'tanken', ipa: '²tɑŋkən', gloss: 'la pensée' },
    note: 'Même graphie, sens distingué uniquement par le tonème.' },
  { id: 'h042', contrast: 'tone-1-vs-2', cefr: 'B1.1',
    a: { word: 'hender', ipa: '¹hɛnːər', gloss: 'arrive, se passe' },
    b: { word: 'hender', ipa: '²hɛnːər', gloss: 'mains' },
    note: 'Le français n’a pas de tons lexicaux : écoutez attentivement la mélodie.' },

  // --- retroflex assimilation (rd, rt, rn, rs, rl) ---
  { id: 'h050', contrast: 'retroflex', cefr: 'A1.3',
    a: { word: 'barn', ipa: 'bɑːɳ', gloss: 'enfant' },
    b: { word: 'ban',  ipa: 'bɑːn', gloss: '(non-mot)' },
    note: '« rn » → /ɳ/ : un seul son, la langue se courbe vers l’arrière.' },
  { id: 'h051', contrast: 'retroflex', cefr: 'A2.1',
    a: { word: 'kort',  ipa: 'kɔʈ', gloss: 'court / carte' },
    b: { word: 'kot',   ipa: 'kɔt', gloss: '(non-mot)' },
    note: '« rt » → /ʈ/ : pas de « r » séparé audible.' },
  { id: 'h052', contrast: 'retroflex', cefr: 'A2.2',
    a: { word: 'norsk', ipa: 'nɔʂk', gloss: 'norvégien' },
    b: { word: 'nosk',  ipa: 'nɔsk', gloss: '(non-mot)' },
    note: '« rs » → /ʂ/ : son rétroflexe, langue relevée.' },
  { id: 'h053', contrast: 'retroflex', cefr: 'A2.3',
    a: { word: 'verden', ipa: 'ˈvæɖən', gloss: 'le monde' },
    b: { word: 'veden',  ipa: 'ˈveːdən', gloss: 'le bois (de chauffage)' },
    note: '« rd » → /ɖ/ : un seul son rétroflexe. À Stavanger, le /r/ est uvulaire et la rétroflexion disparaît.' },
];

export function pairsByContrast(contrast: MinimalPair['contrast']): MinimalPair[] {
  return HVPT_PAIRS.filter((p) => p.contrast === contrast);
}
